import React, { useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import "./TabList.css";
import { ReactComponent as ArrowIcon } from "../../../assets/icons/arrow-right.svg";
import { useLocation } from "react-router-dom";
import { modifyHeader, restoreDefaultHeader, debounce } from "../../../utils/utilFunctions";
import { COLORS } from '../../../utils/colors';
import { Tab } from '../../../types/navigation';
import { useNavigationTranslation } from '../../../hooks/useNavigationTranslation';

interface TabListProps {
  tabs: Tab[];
  activeTab: string | null;
  handleTabClick: (tabId: string) => void;
  handleTabHover: (tabId: string) => void;
  isMobile: boolean;
  subtabsRef: React.RefObject<HTMLDivElement>;
  tabsRef: React.RefObject<HTMLUListElement>;
  bgImgRef: React.RefObject<HTMLDivElement>;
  headerRef: React.RefObject<HTMLDivElement>;
}

// Type for the debounced function
type DebouncedFunction<T extends (...args: any) => any> = {
  (...args: Parameters<T>): void;
  cancel: () => void;
};

const TabList: React.FC<TabListProps> = ({
  tabs,
  activeTab,
  handleTabClick,
  handleTabHover,
  isMobile,
  subtabsRef,
  tabsRef,
  bgImgRef,
  headerRef
}) => {
  const { getTabLabel } = useNavigationTranslation();

  // Create a single persistent debounced function
  const debouncedHover = useRef<DebouncedFunction<typeof handleTabHover>>(
    debounce(handleTabHover, 450)
  );

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      debouncedHover.current.cancel();
    };
  }, []);

  const handleInteraction = (tabId: string) => {
    if (isMobile) {
      handleTabClick(tabId);
    } else {
      debouncedHover.current(tabId);
    }
  };

  const handleTabMouseLeave = () => {
    debouncedHover.current.cancel();
  };

  const clickOnlyTabs = ["tourmalines", "philosophy", "stores"];
  const location = useLocation();
  const TabsEl = tabsRef && tabsRef.current;
  const bgImgEl = document.querySelector('.background-cover');
  const headerEl = headerRef.current;
  const isWatchPage = location.pathname.includes('watches');

  const handleMouseEnter = (e: React.MouseEvent) => {
    const subTabEl: HTMLElement | null = subtabsRef.current;
    if (activeTab === 'maison') {
      if (subTabEl) subTabEl.style.display = 'block'
    }
    if (isWatchPage && TabsEl) {
      TabsEl.style.backgroundColor = COLORS.BACKGROUND;
      TabsEl.style.color = COLORS.OFF_WHITE;
    }
  }

  const handleMouseLeave = (e: React.MouseEvent) => {
    let shouldUpdateTabsStyle = isWatchPage && (e.relatedTarget === bgImgEl || e.relatedTarget === headerEl);
    if (shouldUpdateTabsStyle && TabsEl) {
      TabsEl.style.backgroundColor = "transparent";
      TabsEl.style.color = COLORS.OFF_WHITE;
    }
  }

  return (
    <ul className="tabs" ref={tabsRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <li className="tab-logo">
        <Link to="/" onClick={() => handleTabClick('')}>
          <img src="/logo192.png" alt="Logo" className="tab-logo-image" />
        </Link>
      </li>
      {tabs.filter(tab => !tab.isHidden).map((tab) => (
        <li
          key={tab.id}
          className={`tab-item ${activeTab === tab.id ? "active" : ""}`}
          onClick={() => handleTabClick(tab.id)}
          onMouseEnter={() => handleInteraction(tab.id)}
          onMouseLeave={handleTabMouseLeave}
        >
          <span className="tab-label">{getTabLabel(tab.id)}</span>
          {isMobile && !clickOnlyTabs.includes(tab.id) ? (<span className="arrow-icon">
            <ArrowIcon />
          </span>) : null}
        </li>
      ))}
    </ul>
  );
};

export default TabList;
